import { measureInCanvas } from "./canvas.helpers"
import {
    IDENTITY,
    isAxisAligned,
    LinearTransform,
    multiplyTransforms,
} from "./orientedResize.helpers"
import type { ObjectsById } from "./wrapper.helpers"
import { collectObjectIds } from "./wrapper.helpers"

/**
 * Measuring the selection chrome (outline + handles) off the screen. The DOM is the only
 * place that knows where an object is really drawn — rotation, borders, padding and every
 * other transform included.
 */

export type Rect = {
    top: number
    left: number
    width: number
    height: number
}

export type Chrome = {
    /** the union box of a multiselection, or the object's own box. Absolute canvas units. */
    box: Rect
    /**
     * One box per selected object, relative to `box` — empty unless multiselecting.
     *
     * Figma outlines the members as well as the group, which is the only way to tell WHICH
     * objects a bounding rectangle is made of when it also spans unselected ones.
     */
    members: MemberOutline[]
    /**
     * A single turned object only: `box` is then the object's own, unturned box around the
     * point it is drawn at, and this is how it is turned on the canvas — the chrome is drawn
     * through it so the handles sit on the object's sides (see measureOrientedChrome).
     */
    orientation?: Orientation
}

/**
 * One selected object's outline. A turned object is outlined turned, the way Figma draws it:
 * the rect is then its own unturned box around the point it is drawn at, and `transform`
 * turns it about its centre. An ellipse is outlined as an ellipse.
 */
export type MemberOutline = Rect & {
    transform?: LinearTransform
    ellipse?: boolean
}

export type Orientation = {
    /** everything that turns the object on the canvas: its own transform and its parents' */
    total: LinearTransform
    /** the object's own transform — what its parent-relative styles are turned by */
    own: LinearTransform
}

/**
 * The rendered box of `element` in absolute canvas units — what the chrome is drawn around.
 *
 * Measured rather than read from the styles: a flex child is placed by its parent's layout,
 * a text object sizes itself, and a content-box object with a border or padding renders
 * larger than its width/height say.
 */
export const measureRect = (
    element: HTMLElement,
    canvasElement: HTMLElement,
    scale: number
): Rect => measureInCanvas(element, canvasElement.getBoundingClientRect(), scale)

/**
 * Where a turned ellipse reaches to: the bounds of the ellipse itself, not of its turned box.
 * At 45° the box's bounds reach well past the ellipse at every side — a selection framed by
 * them looks loose and resizes a box nothing fills.
 *
 * An ellipse with half axes (a, b) turned by M spans ±√((m₁₁a)² + (m₁₂b)²) across and
 * ±√((m₂₁a)² + (m₂₂b)²) down from its centre.
 */
const turnedEllipseBounds = (box: Rect, m: LinearTransform): Rect => {
    const a = box.width / 2
    const b = box.height / 2
    const halfWidth = Math.hypot(m.a * a, m.c * b)
    const halfHeight = Math.hypot(m.b * a, m.d * b)
    const centreX = box.left + a
    const centreY = box.top + b
    return {
        left: centreX - halfWidth,
        top: centreY - halfHeight,
        width: halfWidth * 2,
        height: halfHeight * 2,
    }
}

/**
 * The chrome of a multiselection, measured off the screen like a single object's: the union
 * of every selected object AND its descendants (a child can stick out of its parent), with an
 * outline per selected object. Rotations, borders and any other transform are included —
 * the styles know nothing about those.
 *
 * Framed the way Figma frames it: by where each object's SHAPE reaches — a turned ellipse by
 * the ellipse, a turned box by its corners — and each selected object outlined as drawn,
 * turned with it (see MemberOutline).
 *
 * Null when an element is missing (not rendered yet), so the caller can fall back to the
 * styles. An element that is not laid out at all (display: none) has no box and is skipped.
 */
export const measureMultiChrome = (
    selectedIds: string[],
    objectsById: ObjectsById,
    canvasElement: HTMLElement,
    scale: number,
    getElement: (id: string) => HTMLElement | null | undefined
): Chrome | null => {
    const bounds = new Map<string, Rect>()
    const outlines = new Map<string, MemberOutline>()
    for (const selectedId of selectedIds) {
        for (const id of collectObjectIds(selectedId, objectsById)) {
            const element = getElement(id)
            if (!element) return null
            if (element.getClientRects().length === 0) continue

            const ellipse = objectsById[id]?.type === "ellipse"
            const turned = measureOrientedChrome(element, canvasElement, scale)
            if (turned?.orientation) {
                const { total } = turned.orientation
                bounds.set(
                    id,
                    ellipse
                        ? turnedEllipseBounds(turned.box, total)
                        : measureRect(element, canvasElement, scale)
                )
                outlines.set(id, { ...turned.box, transform: total, ellipse })
            } else {
                const rect = measureRect(element, canvasElement, scale)
                bounds.set(id, rect)
                outlines.set(id, { ...rect, ellipse })
            }
        }
    }
    if (bounds.size === 0) return null

    const all = Array.from(bounds.values())
    const left = Math.min(...all.map((rect) => rect.left))
    const top = Math.min(...all.map((rect) => rect.top))
    const right = Math.max(...all.map((rect) => rect.left + rect.width))
    const bottom = Math.max(...all.map((rect) => rect.top + rect.height))

    return {
        box: { left, top, width: right - left, height: bottom - top },
        members: selectedIds
            .map((id) => outlines.get(id))
            .filter((outline): outline is MemberOutline => outline !== undefined)
            .map((outline) => ({ ...outline, left: outline.left - left, top: outline.top - top })),
    }
}

/** The linear part of an element's own computed `transform`. */
const readOwnTransform = (element: Element): LinearTransform => {
    const transform = getComputedStyle(element).transform
    if (!transform || transform === "none") return IDENTITY
    const { a, b, c, d } = new DOMMatrixReadOnly(transform)
    return { a, b, c, d }
}

/**
 * What the element's ancestors, up to the canvas, do to it — the parent objects' rotation,
 * scale and skew, whether set in their styles or by an animation. The element's own
 * transform is left out: its `top` / `left` live in exactly this space.
 */
export const ancestorsTransform = (
    element: HTMLElement,
    canvasElement: HTMLElement | null | undefined
): LinearTransform => {
    let total = IDENTITY
    for (
        let ancestor = element.parentElement;
        ancestor && ancestor !== canvasElement;
        ancestor = ancestor.parentElement
    ) {
        total = multiplyTransforms(readOwnTransform(ancestor), total)
    }
    return total
}

/**
 * The chrome of a single object that is turned on the canvas — by its own rotation or by a
 * turned parent, a quarter turn included — or null when it is not turned at all (a flip or
 * a scale alone), where `measureRect` frames it exactly.
 *
 * The box measured off the screen would be the turned object's bounding box, bigger than the
 * object and with nothing on its edges to grab. Instead the box is the object's own size
 * (its layout size, which no transform touches) centred where the object is drawn, and the
 * transform to draw it through comes with it.
 */
export const measureOrientedChrome = (
    element: HTMLElement,
    canvasElement: HTMLElement,
    scale: number
): Chrome | null => {
    const own = readOwnTransform(element)
    const total = multiplyTransforms(ancestorsTransform(element, canvasElement), own)
    if (isAxisAligned(total)) return null

    // a turn moves the box around its transform origin, but never its centre off the
    // centre of what is painted — the bounding box shares that centre
    const bounds = measureRect(element, canvasElement, scale)
    const width = element.offsetWidth
    const height = element.offsetHeight
    return {
        box: {
            left: bounds.left + bounds.width / 2 - width / 2,
            top: bounds.top + bounds.height / 2 - height / 2,
            width,
            height,
        },
        members: [],
        orientation: { total, own },
    }
}
